// Pagamento FORA da divisão — por valor (nada marcado), produtos marcados
// inteiros ou um pedaço deles (valor digitado menor) — e o desconto da tela.
//
// Decisão do dono (21/10/2026, achado 503): o desconto que está na janela é
// concedido INTEIRO ao lançar o pagamento, parcial ou não. Vira R$ na hora
// (10% "sobre o que falta" = 10% do que faltava naquele momento), fica gravado
// em `mesa.pagamentos.desconto_acumulado` — o mesmo registro das partes pagas
// de uma divisão — e, como lá, é "consumido" no que já está quitado: entra em
// `partial_paid` (por valor) ou no pago de cada produto marcado. Assim:
//  - reabrir a janela mostra o "Restante" já líquido e o desconto como "já
//    aplicado" (não se aplica de novo; um desconto novo é a mais, sobre o que falta);
//  - o cupom de qualquer botão lê o desconto gravado (`contaDoCupom`);
//  - o fechamento final rateia só o acumulado — a rodada já está dentro dele;
//  - produto lançado depois não mexe no valor gravado (congelado em R$).
// Antes só a divisão e o pedaço com % gravavam; R$, cupom e % por valor se
// perdiam ao fechar a janela, e a mesa cobrava de novo o que já tinha abatido
// (Mesa 99 TESTE: R$ 40 de desconto, cupom "FALTA PAGAR 59,00" em vez de 19,00).
import { efeitoNosItens } from "./desfazer";
import { pagarSelecionados, pagarValorNosSelecionados, pagoParcialItem, somarDesconto, valorItem } from "./divisao";

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => Math.round(c) / 100;
const somaC = (lista) => lista.reduce((s, v) => s + centavos(v), 0);

/** Desconto já concedido na sessão (gravado em `desconto_acumulado`), em R$. */
export const descontoJaConcedido = (acumulado) => reais(Math.max(0, centavos(acumulado?.valor)));

/**
 * Quitado da mesa: preço cheio dos produtos pagos + parciais + "por valor". O
 * desconto concedido está dentro (é assim que o Restante sai líquido); o
 * dinheiro de verdade é isto menos `descontoJaConcedido`.
 */
export const quitadoDaMesa = (items, partialPaid) =>
  reais(somaC(items.map((i) => (i._paid ? valorItem(i) : pagoParcialItem(i)))) + centavos(partialPaid));

// Meta que vai para o pedido no fechamento (tipo, % e cupom). Vale a do último
// desconto concedido (`somarDesconto` mescla).
function metaDoDesconto(d) {
  const tipo = d?.tipo && d.tipo !== "none" ? d.tipo : "fixed";
  return {
    tipo,
    percentual: tipo === "percent" ? Number(d?.percentual) || 0 : null,
    cupom: tipo === "coupon" ? d?.cupom || null : null,
  };
}

/**
 * Lança um pagamento fora da divisão.
 *  - `items`, `partialPaid`, `descontoAcumulado`: estado da janela;
 *  - `valorPago`: soma das formas desta rodada (o digitado, se foi editado);
 *  - `calculado`: o Total a pagar que a tela calculou (base da rodada −
 *    desconto). Com produto marcado, valor digitado abaixo dele paga só um pedaço;
 *  - `desconto`: `{ valor, tipo, percentual, cupom }` da tela — `valor` já em R$
 *    sobre o que está sendo pago (0 ou null = sem desconto);
 *  - `forma`, `formaCompleta`, `pagamentoId`: rótulos e id do log.
 * Devolve `{ items, partialPaid, descontoAcumulado, descontoAgora, efeito, tudoPago }`.
 *
 * Sem pagamento nenhum (`valorPago` 0 e nada marcado — "Fechar de novo" da mesa
 * reaberta, "encerrar a mesa"), o desconto só é concedido se com ele a mesa
 * quita; senão nada muda e ele fica na tela — não haveria pagamento no log
 * para a lixeira desfazer.
 */
export function lancarRodada({ items, partialPaid, descontoAcumulado, valorPago, calculado, desconto, forma, formaCompleta, pagamentoId }) {
  const marcados = items.some((i) => i._selected && !i._paid);
  const valorC = Math.max(0, centavos(valorPago));
  let descontoC = Math.max(0, centavos(desconto?.valor));
  const rotulos = { forma, formaCompleta, pagamentoId };
  let novosItens = items;
  let partialC = centavos(partialPaid);
  if (marcados) {
    // Pedaço: o produto recebe o dinheiro + o desconto (fica em aberto pelo
    // resto, sem desconto). Inteiro: quita, e o desconto fica só no registro.
    const pedaco = valorC < centavos(calculado);
    novosItens = pedaco
      ? pagarValorNosSelecionados(items, reais(valorC + descontoC), rotulos)
      : pagarSelecionados(items, rotulos);
  } else {
    const totalC = somaC(items.map(valorItem));
    const quitadoC = centavos(quitadoDaMesa(items, partialPaid)) + valorC;
    const quita = quitadoC + descontoC >= totalC - 1;
    if (valorC === 0 && !quita) descontoC = 0;
    partialC += valorC + descontoC;
    if (quita) novosItens = items.map((i) => (i._paid ? i : { ...i, _paid: true, _selected: false, _paymentInfo: formaCompleta }));
  }
  return {
    items: novosItens,
    partialPaid: reais(partialC),
    descontoAcumulado: descontoC > 0 ? somarDesconto(descontoAcumulado, reais(descontoC), metaDoDesconto(desconto)) : descontoAcumulado || null,
    descontoAgora: reais(descontoC),
    // O que este pagamento fez — é o que a lixeira desfaz
    efeito: {
      itens: efeitoNosItens(items, novosItens),
      porValor: marcados ? 0 : reais(valorC),
      divisaoId: null,
      concluiu: false,
      desconto: reais(descontoC),
    },
    tudoPago: novosItens.every((i) => i._paid),
  };
}

/**
 * Lixeira: o que apagar um pagamento devolve ao "por valor" e ao desconto
 * gravado. O desconto concedido junto com um pagamento por valor saiu do
 * `partial_paid` também; o concedido em produto marcado (ou parte de divisão)
 * volta pelos produtos (`desfazerPagamento`).
 */
export function desfazerNaRodada({ partialPaid, descontoAcumulado }, efeito) {
  const porValorC = centavos(efeito?.porValor);
  const descontoC = centavos(efeito?.desconto);
  const foiPorValor = porValorC > 0 || (!efeito?.divisaoId && Object.keys(efeito?.itens || {}).length === 0);
  const partialC = Math.max(0, centavos(partialPaid) - porValorC - (foiPorValor ? descontoC : 0));
  const ficaC = centavos(descontoAcumulado?.valor) - descontoC;
  const acumulado = descontoC > 0 ? (ficaC > 0 ? { ...descontoAcumulado, valor: reais(ficaC) } : null) : descontoAcumulado || null;
  return { partialPaid: reais(partialC), descontoAcumulado: acumulado };
}
